import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

interface Service {
  slug: string;
  title: string;
  shortDescription: string;
}

interface RelatedServicesProps {
  currentServiceSlug: string;
  locationSlug?: string;
  locationName?: string;
}

// Service definitions - prioritize commercial painting
// IMPORTANT: These slugs MUST match the service slugs defined in ServicePage.tsx
const allServices: Service[] = [
  {
    slug: "commercial-painting",
    title: "Commercial Painting",
    shortDescription: "Professional painting for businesses, offices, and commercial properties"
  },
  {
    slug: "interior-exterior-painting",
    title: "Interior & Exterior Painting",
    shortDescription: "Comprehensive painting services for complete property transformation"
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    shortDescription: "Protect and beautify your property with expert exterior painting"
  },
  {
    slug: "cabinet-painting",
    title: "Cabinet Painting",
    shortDescription: "Refresh kitchens and bathrooms with professional cabinet refinishing"
  },
  {
    slug: "pressure-washing",
    title: "Pressure Washing",
    shortDescription: "Clean and restore surfaces before painting or as standalone service"
  },
  {
    slug: "roof-coating-repair",
    title: "Roof Coating & Repair",
    shortDescription: "Protect your roof with flame-retardant coatings and repairs"
  },
  {
    slug: "waterproofing",
    title: "Waterproofing",
    shortDescription: "Protect your property from water damage with expert waterproofing"
  },
  {
    slug: "drywall-repair",
    title: "Drywall Repair",
    shortDescription: "Professional drywall repair and finishing services"
  },
  {
    slug: "trim-molding",
    title: "Trim & Molding Painting",
    shortDescription: "Enhance architectural details with professional trim painting"
  },
  {
    slug: "deck-staircase-repair",
    title: "Deck & Staircase Repair",
    shortDescription: "Restore and protect outdoor wood structures with expert care"
  },
  {
    slug: "venetian-plaster",
    title: "Venetian Plaster",
    shortDescription: "Luxury Italian marble-dust finish with luminous depth and texture"
  },
  {
    slug: "lime-wash",
    title: "Lime Wash",
    shortDescription: "Traditional mineral finish for stucco and masonry with organic patina"
  }
];

export default function RelatedServices({ 
  currentServiceSlug, 
  locationSlug, 
  locationName 
}: RelatedServicesProps) {
  // Filter out current service
  const availableServices = allServices
    .filter(service => service.slug !== currentServiceSlug);
  
  // Shuffle services to rotate through all of them
  // Fisher-Yates shuffle algorithm for random selection
  const shuffleServices = (services: Service[]) => {
    const shuffled = [...services];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };
  
  // Get 3 random services, but always prioritize commercial painting if available
  // Memoize to stabilize the selection for schema consistency
  const relatedServices = useMemo(() => {
    const shuffled = shuffleServices(availableServices);
    const commercialPainting = shuffled.find(s => s.slug === 'commercial-painting');
    const others = shuffled.filter(s => s.slug !== 'commercial-painting');
    
    return commercialPainting 
      ? [commercialPainting, ...others.slice(0, 2)]
      : shuffled.slice(0, 3);
  }, [currentServiceSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const locationSuffix = locationSlug ? `/${locationSlug}` : "";

  // Build full URLs for schema
  const relatedUrls = relatedServices.map(
    (s) => `https://danispainting.net/service/${s.slug}${locationSuffix}`
  );

  // Inject ItemList + relatedLink schema
  useEffect(() => {
    if (relatedServices.length === 0) return;

    const schema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Related Painting Services${locationName ? ` in ${locationName}` : ""}`,
      "description": `Professional painting services related to ${currentServiceSlug.replace(/-/g, " ")}${locationName ? ` in ${locationName}` : ""}`,
      "numberOfItems": relatedServices.length,
      "itemListElement": relatedServices.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": service.title,
        "description": service.shortDescription,
        "url": `https://danispainting.net/service/${service.slug}${locationSuffix}`,
        "item": {
          "@type": "Service",
          "name": `${service.title}${locationName ? ` in ${locationName}` : ""}`,
          "serviceType": service.title,
          "url": `https://danispainting.net/service/${service.slug}${locationSuffix}`,
          "provider": {
            "@type": "ProfessionalService",
            "name": "Danis Painting Co.",
            "url": "https://danispainting.net"
          }
        }
      })),
      "relatedLink": relatedUrls
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "structured-data-related-services";

    const existing = document.getElementById(script.id);
    if (existing) existing.remove();
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById("structured-data-related-services");
      if (el) el.remove();
    };
  }, [relatedServices, currentServiceSlug, locationSlug, locationName, locationSuffix, relatedUrls]);

  if (relatedServices.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container px-4">
        <div className="text-center mb-12">
          <p className="text-[#1e3a8a] font-semibold mb-2 text-sm tracking-wider">
            EXPLORE MORE SERVICES
          </p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#0a1628] mb-4">
            Related Painting Services{locationName ? ` in ${locationName}` : ""}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover our full range of professional painting services
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {relatedServices.map((service) => (
            <Link
              key={service.slug}
              href={`/service/${service.slug}${locationSuffix}`}
            >
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer group">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0a1628] mb-3 group-hover:text-[#4caf30] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {service.shortDescription}
                  </p>
                  <div className="flex items-center text-[#4caf30] font-semibold text-sm group-hover:translate-x-1 transition-transform">
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
